/**
 * Monitoring Configuration
 *
 * This module initializes and configures Sentry for error tracking and monitoring.
 * It also provides utility functions for manually reporting errors.
 */
import * as Sentry from '@sentry/browser';

// Environment information
const environment = import.meta.env.VITE_ENV || 'development';
const release = import.meta.env.VITE_APP_VERSION || '0.0.1';
const dsn = import.meta.env.VITE_SENTRY_DSN || '';

/**
 * Initialize Sentry for error monitoring
 *
 * @returns {boolean} Whether Sentry was successfully initialized
 */
export function initializeMonitoring(): boolean {
  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn('Sentry DSN not provided, error monitoring is disabled');
    return false;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      release,
      integrations: [Sentry.browserTracingIntegration()],
      tracePropagationTargets: ["https://modelmatch.win"],
      // Adjust sample rate as needed (1.0 means 100% of transactions are captured)
      tracesSampleRate: environment === 'production' ? 0.2 : 1.0,
      // Capture errors only in production by default
      enabled: environment === 'production',
    });

    // Set initial user information (can be updated later)
    Sentry.setUser(null);

    console.log(`Sentry initialized successfully in ${environment} environment`);
    return true;
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
    return false;
  }
}

/**
 * Capture an exception and send to Sentry
 *
 * @param {Error} error - The error object to capture
 * @param {Record<string, any>} [context] - Additional context for the error
 */
export function captureException(error: Error, context?: Record<string, any>): void {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Set user information for better error tracking
 *
 * @param {Object} user - User information
 * @param {string} [user.id] - User ID
 * @param {string} [user.email] - User email
 * @param {string} [user.username] - User username
 */
export function setUserInfo(user: { id?: string; email?: string; username?: string } | null): void {
  Sentry.setUser(user);
}

/**
 * Clear user information from error tracking
 */
export function clearUserInfo(): void {
  Sentry.setUser(null);
}

export default {
  initializeMonitoring,
  captureException,
  setUserInfo,
  clearUserInfo,
};
